import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Form, Input, Space, Steps, Typography, Alert } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useGuestSession } from '../../services/useGuestSession';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const { Title, Paragraph } = Typography;

type Step = 'phone' | 'otp' | 'done';

export default function GuestPhoneChangePage() {
  const navigate = useNavigate();
  const session = useGuestSession();

  const [step, setStep] = useState<Step>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPhone, setNewPhone] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');

  const fingerprint = (window as any).__fpjs_fingerprint ?? Math.random().toString(36).slice(2);

  if (!session.isActive) {
    return (
      <div style={{ maxWidth: 480, margin: '40px auto', padding: '0 16px' }}>
        <Alert type="warning" message="Sessão não encontrada" description="Faça login com seu telefone primeiro." showIcon />
      </div>
    );
  }

  async function handleSendOtp() {
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/guest/phone-change/send`,
        { newPhone, fingerprint },
        { headers: { Authorization: `Bearer ${session.token}` } },
      );
      setChallengeId(data.challengeId);
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Erro ao enviar código.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    setError(null);
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/api/guest/phone-change/verify`,
        { challengeId, code },
        { headers: { Authorization: `Bearer ${session.token}` } },
      );
      setStep('done');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Código incorreto ou expirado.');
    } finally {
      setLoading(false);
    }
  }

  const stepIndex = step === 'phone' ? 0 : step === 'otp' ? 1 : 2;

  return (
    <div style={{ maxWidth: 480, margin: '40px auto', padding: '0 16px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            <MobileOutlined /> Alterar telefone
          </Title>

          <Steps
            current={stepIndex}
            size="small"
            items={[{ title: 'Novo telefone' }, { title: 'Código' }, { title: 'Concluído' }]}
          />

          {error && <Alert message={error} type="error" showIcon />}

          {step === 'phone' && (
            <Form layout="vertical" onFinish={handleSendOtp}>
              <Paragraph type="secondary">Informe o novo número de telefone para associar ao seu perfil.</Paragraph>
              <Form.Item label="Novo telefone (com DDI)" required>
                <Input
                  placeholder="+55 11 99999-0000"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value.replace(/\s/g, ''))}
                  size="large"
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading} disabled={!newPhone}>
                Enviar código
              </Button>
            </Form>
          )}

          {step === 'otp' && (
            <Form layout="vertical" onFinish={handleVerifyOtp}>
              <Paragraph type="secondary">
                Insira o código enviado para <strong>{newPhone}</strong>.
              </Paragraph>
              <Form.Item label="Código de verificação" required>
                <Input
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  size="large"
                  maxLength={6}
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading} disabled={code.length !== 6}>
                Verificar
              </Button>
            </Form>
          )}

          {step === 'done' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert type="success" message="Telefone alterado com sucesso!" showIcon />
              <Button type="primary" block size="large" onClick={() => navigate(-1)}>
                Voltar
              </Button>
            </Space>
          )}
        </Space>
      </Card>
    </div>
  );
}
